import React, { useState, useEffect } from 'react';

function BatchDetailsModal({ batch, onClose, onBack }) {
  if (!batch) return null;

  const [isNewBatch, setIsNewBatch] = useState(false);

  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return '';

    // Handle Firebase Timestamp objects
    if (date && typeof date === 'object' && date.seconds) {
      const jsDate = new Date(date.seconds * 1000);
      return jsDate.toLocaleDateString('en-US');
    }

    // Handle regular Date objects
    if (date instanceof Date) {
      return date.toLocaleDateString('en-US');
    }

    // Handle string dates
    if (typeof date === 'string') {
      return date;
    }

    return '';
  };

  useEffect(() => {
    const batchId = batch.batchid || batch.id || '';
    const newBatchPattern = /^[0-9]{4,8}-[A-Za-z0-9]{2,10}$/;
    setIsNewBatch(newBatchPattern.test(batchId));
  }, [batch]);

  // Extract filename from originalDocumentURL
  const getFilename = (url) => {
    if (!url) return 'N/A';
    const parts = url.split('/');
    return parts[parts.length - 1] || 'document';
  };

  const handleViewDocument = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="batch-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Batch Details</h2>
          <div className="header-buttons">
            <button className="back-button" onClick={onBack}>
              ← Back
            </button>
            <button className="close-button" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        <div className="modal-body">
          {/* At a glance section */}
          <div className="at-a-glance-section">
            <h3>At a glance:</h3>
            <div className="glance-grid">
              <div className="glance-row">
                <div className="glance-item">
                  <span className="glance-label">Start Date</span>
                  <span className="glance-value">
                    {formatDate(batch['Date Received']) || 'March 1, 2026'}
                  </span>
                </div>
                <div className="glance-item">
                  <span className="glance-label">Batch ID</span>
                  <span className="glance-value">
                    {batch.batchid || batch.id || 'ABCD-1234567'}
                  </span>
                </div>
                <div className="glance-item">
                  <span className="glance-label">Group ID</span>
                  <span className="glance-value">{batch.groupid || 'N/A'}</span>
                </div>
              </div>
              <div className="glance-row">
                <div className="glance-item">
                  <span className="glance-label">Last Analytical</span>
                  <span className="glance-value">
                    {formatDate(batch.sampleDate) || '03/01/2026'}
                  </span>
                </div>
                <div className="glance-item">
                  <span className="glance-label">Customer</span>
                  <span className="glance-value">{batch.Customer || 'Acme Refining'}</span>
                </div>
                <div className="glance-item">
                  <span className="glance-label">BOL Volume (gal)</span>
                  <span className="glance-value">{batch['BOL Volume'] || '15,000'}</span>
                </div>
              </div>
              <div className="glance-row">
                <div className="glance-item">
                  <span className="glance-label">Location added</span>
                  <span className="glance-value">Sacramento Station</span>
                </div>
                <div className="glance-item">
                  <span className="glance-label">Meter Volume (gal)</span>
                  <span className="glance-value">{batch['Metered Volume'] || '14,990'}</span>
                </div>
                <div className="glance-item">
                  <span className="glance-label">Sulfur (mg/kg)</span>
                  <span className="glance-value">{batch.sulfur || '8'}</span>
                </div>
              </div>
              <div className="glance-row">
                <div className="glance-item">
                  <span className="glance-label">Current Location</span>
                  <span className="glance-value">{batch.Location || 'Pelham Junction'}</span>
                </div>
                <div className="glance-item">
                  <span className="glance-label">Product</span>
                  <span className="glance-value">{batch.Product || 'Gasoline'}</span>
                </div>
                <div className="glance-item">
                  <span className="glance-label">RVP (psi)</span>
                  <span className="glance-value">{batch.rvp || '8.8'}</span>
                </div>
              </div>
              <div className="glance-row">
                <div className="glance-item"></div>
                <div className="glance-item"></div>
                <div className="glance-item">
                  <span className="glance-label">Benzene (ppm)</span>
                  <span className="glance-value">{batch.benzene || '0.5'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Workflows section */}
          {!isNewBatch && (
            <div className="table-section">
              <h3>Workflows:</h3>
              <div className="table-wrapper">
                <table className="workflows-table">
                  <thead>
                    <tr>
                      <th>Workflow</th>
                      <th>Initiate</th>
                      <th>Date Initiated</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>90 Grade Tank Blendstock Oversight Testing</td>
                      <td>
                        <button className="click-button">Click</button>
                      </td>
                      <td>N/A</td>
                      <td>N/A</td>
                    </tr>
                    <tr>
                      <td>98 Grade Tank Blendstock Oversight</td>
                      <td>
                        <button className="click-button">Click</button>
                      </td>
                      <td>N/A</td>
                      <td>N/A</td>
                    </tr>
                    <tr className="workflow-active">
                      <td>Airport Delivery Jet Fuel Manual Line Sample Testing</td>
                      <td>
                        <button className="view-status-button">View Status</button>
                      </td>
                      <td>3/1/26</td>
                      <td className="status-in-process">In process</td>
                    </tr>
                    <tr>
                      <td>Inbound Line Sampling Diesel Scheduled Testing</td>
                      <td>
                        <button className="click-button">Click</button>
                      </td>
                      <td>N/A</td>
                      <td>N/A</td>
                    </tr>
                    <tr>
                      <td>Outbound Line Sampling Diesel Scheduled Testing</td>
                      <td>
                        <button className="click-button">Click</button>
                      </td>
                      <td>N/A</td>
                      <td>N/A</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Documents section */}
          <div className="table-section">
            <h3>Documents:</h3>
            <div className="table-wrapper">
              <table className="documents-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Type</th>
                    <th>Date Added</th>
                    <th>Added by</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {batch.originalDocumentURL ? (
                    <tr>
                      <td>{getFilename(batch.originalDocumentURL)}</td>
                      <td>COA</td>
                      <td>{formatDate(batch.sampleDate) || '3/1/26'}</td>
                      <td>atachovsky</td>
                      <td>
                        <button
                          className="click-button"
                          onClick={() => handleViewDocument(batch.originalDocumentURL)}
                        >
                          Click
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td colSpan="5" className="no-data">
                        No documents available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <button className="add-document-button">Add document</button>
          </div>

          {/* Activity Log section */}
          <div className="table-section">
            <h3>Activity Log:</h3>
            <div className="table-wrapper">
              <table className="activity-log-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Reviewed by</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {isNewBatch ? (
                    <tr>
                      <td>{new Date().toLocaleDateString('en-US')}</td>
                      <td>COA added to system</td>
                      <td>FuelSonar</td>
                      <td></td>
                    </tr>
                  ) : (
                    <>
                      <tr>
                        <td>1/1/26</td>
                        <td>Product arrived at Sacramento</td>
                        <td>atachovsky</td>
                        <td className="status-approved">Approved</td>
                      </tr>
                      <tr>
                        <td>1/1/26</td>
                        <td>BOL and COA added to system</td>
                        <td>htachovsky</td>
                        <td className="status-approved">Approved</td>
                      </tr>
                      <tr>
                        <td>1/2/26</td>
                        <td>BOL and COA approved, product lifted</td>
                        <td>jemison</td>
                        <td className="status-approved">Approved</td>
                      </tr>
                      <tr>
                        <td>2/1/26</td>
                        <td>Analysis of material by SGS, COA added</td>
                        <td>atatchovsky</td>
                        <td className="status-approved">Approved</td>
                      </tr>
                      <tr>
                        <td>3/1/26</td>
                        <td>Sampling Plan approved</td>
                        <td>cmccullough</td>
                        <td className="status-approved">Approved</td>
                      </tr>
                      <tr>
                        <td>3/4/26</td>
                        <td>Sampling of material in Oakland</td>
                        <td>htatchovsky</td>
                        <td className="status-approved">Approved</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BatchDetailsModal;
